import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const ActionDropdown = ({ actions }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight || 200;
    const menuWidth = 192;

    const spaceBelow = window.innerHeight - rect.bottom;
    const showAbove = spaceBelow < menuHeight + 8 && rect.top > menuHeight;

    setPos({
      top: showAbove ? rect.top - menuHeight - 4 : rect.bottom + 4,
      left: Math.max(8, rect.right - menuWidth),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const visibleActions = actions.filter(a => !a.hidden);
  if (visibleActions.length === 0) return null;

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen(prev => !prev)}
        className="p-1.5 rounded-lg hover:bg-dark-100 transition-colors text-dark-400 hover:text-dark-600"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          className="fixed w-48 bg-white rounded-lg shadow-xl border border-dark-100 py-1 z-[9999]"
          style={{ top: pos.top, left: pos.left }}
        >
          {visibleActions.map((action, i) => (
            <React.Fragment key={i}>
              {action.divider && <div className="border-t border-dark-100 my-1" />}
              <button
                onClick={() => { setOpen(false); action.onClick(); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors text-left ${
                  action.variant === 'danger'
                    ? 'text-red-600 hover:bg-red-50'
                    : action.variant === 'success'
                    ? 'text-green-600 hover:bg-green-50'
                    : action.variant === 'warning'
                    ? 'text-amber-600 hover:bg-amber-50'
                    : 'text-dark-700 hover:bg-dark-50'
                }`}
              >
                {action.icon}
                {action.label}
              </button>
            </React.Fragment>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

export default ActionDropdown;
